'use client';

import React, { useEffect, useRef } from 'react';

interface MapMarker {
  latitude: number;
  longitude: number;
  title?: string;
  color?: string;
  useGradient?: boolean;
  googleMapsUrl?: string; // Add Google Maps URL property
}

interface LeafletMapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  markerTitle?: string;
  markerColor?: string;
  darkMode?: boolean;
  markers?: MapMarker[]; // Update type to include googleMapsUrl
  glassmorphism?: boolean;
  useGradientMarkers?: boolean;
  enableGoogleMapsRedirect?: boolean; // Add prop to enable/disable feature
}

export const LeafletMap: React.FC<LeafletMapProps> = ({ 
  latitude, 
  longitude, 
  zoom = 15,
  markerTitle = "Our Location",
  markerColor = "#08264C",
  darkMode = false,
  markers = [],
  glassmorphism = false,
  useGradientMarkers = false,
  enableGoogleMapsRedirect = false // Add new prop with default value
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    // Check if leaflet is already loaded
    if (typeof window !== 'undefined' && (window as any).L) {
      initializeMap();
    } else {
      // Load Leaflet CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);

      // Load Leaflet JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => {
        if (isMountedRef.current) {
          initializeMap();
        }
      };
      document.head.appendChild(script);
    }

    return () => {
      // Clean up
      isMountedRef.current = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      if (mapContainerRef.current) {
        mapContainerRef.current.innerHTML = '';
      }
    };
  }, [latitude, longitude, zoom, markerColor, darkMode, markers, glassmorphism, useGradientMarkers, enableGoogleMapsRedirect]);

  const createCustomIcon = (color: string, useGradient: boolean = false) => {
    const L = (window as any).L;
    
    if (useGradient) {
      // Create improved gradient marker using SVG with better design
      return L.divIcon({
        className: 'custom-gradient-marker',
        html: `
          <div style="
            width: 40px;
            height: 40px;
            position: relative;
          ">
            <svg width="40" height="40" viewBox="0 0 40 40" style="transform: translate(-50%, -100%); position: absolute; top: 0; left: 50%;">
              <defs>
                <linearGradient id="markerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style="stop-color:#08264C;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#3F74EF;stop-opacity:1" />
                </linearGradient>
                <filter id="markerShadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                  <feOffset dx="0" dy="2" result="offsetblur" />
                  <feFlood flood-color="rgba(0,0,0,0.3)" />
                  <feComposite in2="offsetblur" operator="in" />
                  <feMerge>
                    <feMergeNode />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <path 
                d="M20 0C12 0 5 6 5 14C5 22 20 40 20 40C20 40 35 22 35 14C35 6 28 0 20 0Z" 
                fill="url(#markerGradient)" 
                stroke="white" 
                stroke-width="2"
                filter="url(#markerShadow)"
              />
              <circle cx="20" cy="15" r="6" fill="white" />
              <circle cx="20" cy="15" r="3" fill="url(#markerGradient)" />
            </svg>
          </div>
        `,
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40] // Position popup correctly above marker
      });
    } else {
      // Create standard colored marker with improved design
      return L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            background: ${color};
            width: 32px;
            height: 32px;
            border-radius: 50% 50% 50% 0;
            position: relative;
            transform: rotate(-45deg);
            border: 2px solid white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          ">
            <div style="
              width: 12px;
              height: 12px;
              background: white;
              border-radius: 50%;
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(45deg);
            "></div>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32] // Position popup correctly above marker
      });
    }
  };

  const initializeMap = () => {
    if (mapContainerRef.current && (window as any).L && isMountedRef.current) {
      const L = (window as any).L;
      
      // Clear previous map if exists
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      mapContainerRef.current.innerHTML = '';
      
      // Apply glassmorphism effect if requested
      if (glassmorphism && mapContainerRef.current) {
        mapContainerRef.current.style.background = 'rgba(255, 255, 255, 0.1)';
        mapContainerRef.current.style.backdropFilter = 'blur(10px) saturate(180%)';
        // Add vendor prefix for webkit
        (mapContainerRef.current.style as any).webkitBackdropFilter = 'blur(10px) saturate(180%)';
        mapContainerRef.current.style.border = '1px solid rgba(255, 255, 255, 0.2)';
      }
      
      // Create map
      const map = L.map(mapContainerRef.current).setView([latitude, longitude], zoom);
      mapInstanceRef.current = map;
      
      // Choose tile layer based on dark mode
      let tileLayer;
      if (darkMode) {
        // Dark mode tiles
        tileLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 19
        });
      } else {
        // Default light mode tiles
        tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        });
      }
      
      tileLayer.addTo(map);
      
      // Customize popup options for enhanced styling
      const popupOptions = {
        className: 'enhanced-popup',
        offset: L.point(0, -30), // Position popup above marker with better offset
        autoPan: true,
        autoPanPadding: L.point(50, 50),
        closeButton: false, // Remove close button for cleaner look
        autoClose: false, // Keep popup open when clicking elsewhere
        closeOnClick: false,
        maxWidth: 200 // Limit popup width for better appearance
      };
      
      // Add main marker
      const mainIcon = createCustomIcon(markerColor, useGradientMarkers);
      const mainMarker = L.marker([latitude, longitude], { icon: mainIcon }).addTo(map);
      if (markerTitle) {
        mainMarker.bindPopup(`<div class="popup-content">${markerTitle}</div>`, popupOptions).openPopup();
      }
      
      // Add click event to redirect to Google Maps if enabled
      if (enableGoogleMapsRedirect) {
        mainMarker.on('click', function() {
          const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
          window.open(googleMapsUrl, '_blank');
        });
      }
      
      // Add additional markers
      markers.forEach((marker) => {
        const markerIcon = createCustomIcon(marker.color || markerColor, marker.useGradient || useGradientMarkers);
        const additionalMarker = L.marker([marker.latitude, marker.longitude], { icon: markerIcon }).addTo(map);
        if (marker.title) {
          additionalMarker.bindPopup(`<div class="popup-content">${marker.title}</div>`, popupOptions);
        }
        
        // Add click event to redirect to Google Maps if enabled
        if (enableGoogleMapsRedirect) {
          // Use the provided URL or generate a default Google Maps URL
          const googleMapsUrl = marker.googleMapsUrl || 
                               `https://www.google.com/maps/search/?api=1&query=${marker.latitude},${marker.longitude}`;
          
          additionalMarker.on('click', function() {
            window.open(googleMapsUrl, '_blank');
          });
        }
      });
      
      // Add enhanced CSS for popups with glassmorphism
      const style = document.createElement('style');
      style.innerHTML = `
        .enhanced-popup .leaflet-popup-content-wrapper {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px) saturate(180%);
          -webkit-backdrop-filter: blur(10px) saturate(180%);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.37),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
          color: #333;
          padding: 0;
          font-family: 'Space Grotesk', sans-serif;
    }
        .enhanced-popup .leaflet-popup-tip {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px) saturate(180%);
          -webkit-backdrop-filter: blur(10px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.5);
          width: 18px;
          height: 18px;
          margin: -9px auto 0;
          transform: rotate(45deg);
          border-top: none;
          border-left: none;
          position: relative;
          box-shadow: 
            3px 3px 6px rgba(31, 38, 135, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }
        .enhanced-popup .leaflet-popup-content {
          margin: 0;
          padding: 12px 16px;
          color: #333;
          font-weight: 600;
          font-size: 14px;
          line-height: 1.4;
          text-align: center;
          min-width: 120px;
        }
        .popup-content {
          font-weight: 600;
          font-size: 14px;
          line-height: 1.4;
          text-align: center;
          color: #333;
        }
        /* Animation for popup */
        .leaflet-popup {
          animation: popup-fade-in 0.3s ease-out;
        }
        @keyframes popup-fade-in {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        /* Ensure popup is visible above other elements */
        .leaflet-popup {
          z-index: 1000 !important;
        }
        .leaflet-popup-pane {
          z-index: 1000 !important;
        }
      `;
      document.head.appendChild(style);
      
      // Add some styling to the map container
      if (mapContainerRef.current) {
        mapContainerRef.current.style.background = darkMode ? '#1a1a2e' : '#f8f9fa';
        mapContainerRef.current.style.borderRadius = '16px';
        mapContainerRef.current.style.overflow = 'hidden';
      }
      
      // Ensure popups are visible by fitting bounds if needed
      const allMarkers = [
        { latitude, longitude },
        ...markers
      ];
      
      if (allMarkers.length > 1) {
        const group = new L.featureGroup(allMarkers.map(m => L.marker([m.latitude, m.longitude])));
        map.fitBounds(group.getBounds().pad(0.1));
      }
    }
  };

  return (
    <div 
      ref={mapContainerRef} 
      className="w-full h-full rounded-2xl"
      style={{ minHeight: '400px' }}
    />
  );
};